import Vue from 'vue';
import Vuex from 'vuex';
import Dataman from 'App/Dataman';
import InterfaceAPI from 'App/Interface';
import UserModel from 'Models/User';
import UserboxAPI from 'App/Userbox';
import { DEFAULT_LANGUAGE } from 'App/constants/trans';
import { persist as persistValue, restore, timePoint } from 'App/utils/Helpers';
import { CACHED_DATA_CATEGORIES } from '@/app/utils/Collections';

Vue.use(Vuex);

const User = new UserModel(Dataman.User);
const Userbox = new UserboxAPI();
const Interface = new InterfaceAPI();

Dataman.setErrorHandler((err) => {
  if (err?.response?.status === 401) {
    Dataman.cancelRequests();
    setTimeout(() => {
      User.checkAuthToken().then(() => {
        User.logout().then(() => {
          Interface.reloadApp();
        });
      });
    }, 2000);
  } else {
    Interface.CatchError = err + ' | ' + typeof err;
  }
});
Dataman.refreshTokenHandler(async (tokens) => {
  await User.auth(tokens);
});

const DEF_REPORTER_STATE = {
  entity: null,
  items: [],
  subCatId: null,
  templateId: null,
};
const DEF_COUNTERS_CACHE = {
  counters: null,
  lastUpdate: null,
  categoryId: null,
};
const DEF_STAFF_STATS = {
  period: 0,
  custom: { from: null, to: null },
};
const DEF_RECENTLY_ADDED = {
  counters: null,
  items: null,
  lastUpdate: null,
};
const DEF_FILTER_EDITOR = {
  id: null,
  category: null,
  item: null,
  key: null,
  path: null,
};
const DEF_USER_SESSIONS = { loaded: false, list: [], activeId: null };
const DEF_DASHBOARD_COUNTERS_CACHE = {
  counters: {},
};

const DEFAULT_THEME = 'default';

const store = new Vuex.Store({
  state: {
    flags: {
      loaded: false,
      isAdmin: false,
    },
    user: User,
    Interface,
    Userbox,
    reporter: restore('reporter', { ...DEF_REPORTER_STATE }),
    priceRequests: restore('priceRequests', [0, 0, 0]),
    currentRequest: null,
    countersCache: restore('countersCache', { ...DEF_COUNTERS_CACHE }),
    dashboardCountersCache: restore('dashboardCountersCache', { ...DEF_DASHBOARD_COUNTERS_CACHE }),
    recentlyAdded: restore('recentlyAdded', { ...DEF_RECENTLY_ADDED }),
    modules: restore('modules', {}),
    lang: restore('lang', DEFAULT_LANGUAGE),
    filters: restore('filters', {}),
    infoSourceData: null,
    staffStats: restore('staffStats', { ...DEF_STAFF_STATS }),
    cachedData: restore('cachedData', {}),
    vars: restore('vars', {}),
    columnsFilter: restore('columnsFilter', {}),
    visitedItems: restore('history', []),
    isOrderCreatedFlag: false,
    forwardBackwardRedirectEntityData: {
      page: 1,
      pages: 1,
      itemsPerPage: 30,
      category: '',
      items: [],
      prevName: '',
      nextName: '',
      link: null,
      filter: null,
    },
    filterEditor: restore('filterEditor', { ...DEF_FILTER_EDITOR }),
    appTabs: new Map(restore('appTabs', [])),
    userSessions: { ...DEF_USER_SESSIONS },
    featuredSessionDismissed: restore('featuredSessionDismissed', false),

    // Integration API - https://fdc-api.winglegroup.com/
    ingredientsAndNutrients: {
      foodByGtinCode: null,
      isLoadingFoodByGtinCode: false,
      errorLoadFoodByGtinCode: '',
      // dsld food by UPC code
      dsldFoodByUpc: null,
      isLoadingDsldFoodByUpc: false,
      errorLoadDsldFoodByUpc: '',
      // dsld food by brand name
      dsldFoodByBrandName: null,
      isLoadingDsldFoodByBrandName: false,
      errorLoadDsldFoodByBrandName: '',
    },
    activeUserFilter: null,
    theme: restore('theme', DEFAULT_THEME),
  },
  mutations: {
    changeForwardBackwardRedirectEntityData(state, payload) {
      state.forwardBackwardRedirectEntityData = payload;
    },
    changeIsOrderCreatedFlag(state, payload) {
      state.isOrderCreatedFlag = payload;
    },
    addVisitedItem(state, item) {
      state.visitedItems.push(item);
      persistValue('history', state.visitedItems);
    },
    setVar(state, data) {
      state.vars = { ...state.vars, ...(data || {}) };
      persistValue('vars', state.vars);
    },
    removeVar(state, key) {
      if (key in state.vars) {
        delete state.vars[key];
        persistValue('vars', state.vars);
      }
    },
    logout(state) {
      state.flags = { loaded: false, isAdmin: false };
      state.reporter = { ...DEF_REPORTER_STATE };
      state.recentlyAdded = { ...DEF_RECENTLY_ADDED };
      state.filters = {};
      state.infoSourceData = null;
      state.staffStats = { ...DEF_STAFF_STATS };
      state.featuredSessionDismissed = false;
      state.dashboardCountersCache = { ...DEF_DASHBOARD_COUNTERS_CACHE };
      persistValue('recentlyAdded', state.recentlyAdded);
      persistValue('reporter', state.reporter);
      persistValue('filters', state.filters);
      persistValue('staffStats', state.staffStats);
      persistValue('featuredSessionDismissed', state.featuredSessionDismissed);
      persistValue('dashboardCountersCache', state.dashboardCountersCache);
    },
    setPriceRequested(state) {
      if (state.currentRequest !== null) {
        const requests = [...state.priceRequests];
        requests[state.currentRequest] = 1;
        persistValue('priceRequests', requests);
        state.priceRequests = requests;
        state.currentRequest = null;
      }
    },
    setCurrentRequest(state, key) {
      state.currentRequest = key;
    },
    setFlag(state, { flag, value }) {
      state.flags[flag] = value;
    },
    setReporter(state, { items, entity, subCatId, templateId }) {
      state.reporter.entity = entity || null;
      state.reporter.items = items.map((el) => +el);
      if (subCatId !== undefined) {
        state.reporter.subCatId = subCatId || null;
      }
      state.reporter.templateId = templateId || null;
      persistValue('reporter', state.reporter);
    },
    updCounters(state, { counters, categoryId = null }) {
      state.countersCache = { counters, lastUpdate: Date.now(), categoryId };
      persistValue('countersCache', state.countersCache);
    },
    updRecentlyAdded(state, { type, data }) {
      state.recentlyAdded[type] = data;
      if (type === 'counters') {
        state.recentlyAdded.lastUpdate = Date.now();
        state.recentlyAdded.items = null;
      }
      persistValue('recentlyAdded', state.recentlyAdded);
    },
    clearRecentlyAdded(state) {
      state.recentlyAdded = { ...DEF_RECENTLY_ADDED };
      persistValue('recentlyAdded', state.recentlyAdded);
    },
    setDashboardCountersCache(state, { tabId, dateKey, category, total, recently }) {
      if (!state.dashboardCountersCache.counters[tabId]) {
        state.dashboardCountersCache.counters[tabId] = {};
      }
      if (!state.dashboardCountersCache.counters[tabId][dateKey]) {
        state.dashboardCountersCache.counters[tabId][dateKey] = {};
      }
      state.dashboardCountersCache.counters[tabId][dateKey][category] = { total, recently };
      persistValue('dashboardCountersCache', state.dashboardCountersCache);
    },
    clearDashboardCountersCache(state) {
      state.dashboardCountersCache = { ...DEF_DASHBOARD_COUNTERS_CACHE };
      persistValue('dashboardCountersCache', state.dashboardCountersCache);
    },
    setModules(state, data) {
      state.modules = { ...data };
      persistValue('modules', state.modules);
    },
    setLang(state, lang) {
      state.lang = lang;
      persistValue('lang', state.lang);
    },
    setFilter(state, { key, data, persist }) {
      state.filters[key] = data;
      if (persist !== false) {
        persistValue('filters', state.filters);
      }
    },
    setInfoSourceData(state, val) {
      state.infoSourceData = val;
    },
    setStaffStats(state, payload) {
      state.staffStats = { ...state.staffStats, ...payload };
      persistValue('staffStats', state.staffStats);
    },
    setCachedData(state, payload) {
      const { key, data, lifetime } = payload;
      state.cachedData = {
        ...state.cachedData,
        [key]: { data, lifetime, expireAt: timePoint(lifetime) },
      };
      persistValue('cachedData', state.cachedData);
    },
    clearCachedData(state, key) {
      const data = { ...state.cachedData };
      const keys = Array.isArray(key) ? key : [key];
      for (const key of keys) {
        delete data[key];
      }
      state.cachedData = data;
      persistValue('cachedData', state.cachedData);
    },
    setColumnsFilter(state, payload) {
      const { path, config } = payload || {};
      Vue.set(state.columnsFilter, path, config);
      persistValue('columnsFilter', state.columnsFilter);
    },
    setFilterEditor(state, payload) {
      const { moduleAlias, item, key, path } = payload || {};
      state.filterEditor = { id: item?.id || null, category: moduleAlias, item, key, path };
      persistValue('filterEditor', state.filterEditor);
    },
    setTab(state, payload) {
      const { uid } = payload;
      state.appTabs.set(uid, payload);
      persistValue('appTabs', [...state.appTabs.entries()]);
    },
    updateTab(state, payload) {
      const { uid, ...data } = payload;
      const tab = state.appTabs.get(uid);
      if (tab) {
        Object.assign(tab, data);
      } else {
        state.appTabs.set(uid, payload);
      }
      persistValue('appTabs', [...state.appTabs.entries()]);
    },
    updateTabProp(state, payload) {
      const { uid, key, value } = payload;
      const tab = state.appTabs.get(uid);
      tab[key] = value;
      persistValue('appTabs', [...state.appTabs.entries()]);
    },
    removeTab(state, uid) {
      state.appTabs.delete(uid);
      persistValue('appTabs', [...state.appTabs.entries()]);
    },
    clearAllTabs(state) {
      state.appTabs = new Map();
      persistValue('appTabs', [...state.appTabs.entries()]);
    },
    setUserSessions(state, payload) {
      state.userSessions.list = payload;
      state.userSessions.loaded = true;
    },
    pushUserSession(state, payload) {
      state.userSessions.list.unshift(payload);
    },
    updateUserSession(state, payload) {
      const idx = state.userSessions.list.findIndex((el) => el.id === payload.id);
      if (idx === -1) {
        state.userSessions.list.unshift(payload);
      } else {
        state.userSessions.list[idx] = payload;
      }
    },
    setActiveUserSession(state, payload) {
      state.userSessions.activeId = payload;
    },
    dismissFeaturedSession(state, payload) {
      state.featuredSessionDismissed = !!payload;
      persistValue('featuredSessionDismissed', state.featuredSessionDismissed);
    },

    // ingredientsAndNutrients state

    // food by GTIN code
    setFoodByGtinCode(state, payload) {
      state.ingredientsAndNutrients.foodByGtinCode = payload;
    },
    setIsLoadingFoodByGtinCode(state, payload) {
      state.ingredientsAndNutrients.isLoadingFoodByGtinCode = payload;
    },
    setErrorLoadFoodByGtinCode(state, payload) {
      state.ingredientsAndNutrients.errorLoadFoodByGtinCode = payload;
    },

    // dsld food by UPC code
    setDsldFoodByUpc(state, payload) {
      state.ingredientsAndNutrients.dsldFoodByUpc = payload;
    },
    setIsLoadingDsldFoodByUpc(state, payload) {
      state.ingredientsAndNutrients.isLoadingDsldFoodByUpc = payload;
    },
    setErrorLoadDsldFoodByUpc(state, payload) {
      state.ingredientsAndNutrients.errorLoadDsldFoodByUpc = payload;
    },

    // dsld food by brand name
    setDsldFoodByBrandName(state, payload) {
      state.ingredientsAndNutrients.dsldFoodByBrandName = payload;
    },
    setIsLoadingDsldFoodByBrandName(state, payload) {
      state.ingredientsAndNutrients.isLoadingDsldFoodByBrandName = payload;
    },
    setErrorLoadDsldFoodByBrandName(state, payload) {
      state.ingredientsAndNutrients.errorLoadDsldFoodByBrandName = payload;
    },
    setActiveUserFilter(state, payload) {
      state.activeUserFilter = payload;
    },
    setTheme(state, theme) {
      state.theme = theme;
      persistValue('theme', state.theme);
    },
  },
  actions: {
    logout({ commit }) {
      commit('logout');
    },
    priceRequest({ commit }, key) {
      commit('setCurrentRequest', key);
    },
    setRequested({ commit }) {
      commit('setPriceRequested');
    },
    setIsAdmin({ commit }, state) {
      commit('setFlag', { flag: 'isAdmin', value: !!state });
    },
    setFlag({ commit }, { flag, state }) {
      commit('setFlag', { flag, value: !!state });
    },
    setReporter({ commit }, { items, entity = null, subCatId, templateId = null }) {
      commit('setReporter', { items, entity, subCatId, templateId });
    },
    setCounters({ commit }, { counters, categoryId = null } = {}) {
      commit('updCounters', { counters: counters || {}, categoryId });
    },
    updCounter({ commit, state }, { category, value }) {
      commit('updCounters', {
        counters: {
          ...(state.countersCache.counters || {}),
          [category]: value,
        },
        categoryId: state.countersCache.categoryId ?? null,
      });
    },
    setRecentCounters({ commit }, data) {
      commit('updRecentlyAdded', { type: 'counters', data });
    },
    setRecentItems({ commit, state }, data) {
      const current = state.recentlyAdded.items || {};
      commit('updRecentlyAdded', {
        type: 'items',
        data: { ...current, ...data },
      });
    },
    setDashboardCountersCache({ commit }, { tabId, dateKey, category, total, recently }) {
      commit('setDashboardCountersCache', { tabId, dateKey, category, total, recently });
    },
    clearDashboardCountersCache({ commit }) {
      commit('clearDashboardCountersCache');
    },
    setModules({ commit }, data) {
      commit('setModules', data || {});
    },
    setLang({ commit }, lang) {
      commit('setLang', lang);
    },
    updateFilter({ commit, state }, { key, data, persist }) {
      const exists = state.filters[key] || {};
      commit('setFilter', { key, data: { ...exists, ...data }, persist });
    },
    setFilter({ commit }, payload) {
      commit('setFilter', payload);
    },
    clearFilter({ commit }, key) {
      commit('setFilter', { key, data: null });
    },
    setInfoSourceUrl({ commit }, url) {
      commit('setInfoSourceData', { url, loading: false });
    },
    setInfoSourceLoading({ commit, state }, loading) {
      commit('setInfoSourceData', {
        ...(state.infoSourceData || {}),
        loading: !!loading,
      });
    },
    clearInfoSourceData({ commit }) {
      commit('setInfoSourceData', null);
    },
    setStaffStatsFilter({ commit }, { period, custom }) {
      const payload = { period };
      if (custom) {
        payload.custom = custom;
      }
      commit('setStaffStats', payload);
    },
    sendAction() {
      /* noop */
    },
    provideHeaders() {
      /* noop */
    },
    filterColumns({ commit }, payload) {
      commit('setColumnsFilter', payload);
    },
    editUserFilter({ commit }, payload) {
      commit('setFilterEditor', payload);
    },
    unsetFilterEditor({ commit }) {
      commit('setFilterEditor', { ...DEF_FILTER_EDITOR });
    },
    updateTab({ commit }, payload) {
      commit('updateTab', payload);
    },
    // ingredientsAndNutrients state
    async loadFoodByGtinCode({ commit }, gtinCode) {
      commit('setFoodByGtinCode', null);
      commit('setErrorLoadFoodByGtinCode', '');
      commit('setIsLoadingFoodByGtinCode', false);

      try {
        commit('setIsLoadingFoodByGtinCode', true);
        const data = await Dataman.DB.ingredientsAndNutrients.getFoodByGTIN(gtinCode);

        if (data) {
          commit('setFoodByGtinCode', data.data);
        }
      } catch (error) {
        commit('setErrorLoadFoodByGtinCode', 'No data found');
        return;
      } finally {
        commit('setIsLoadingFoodByGtinCode', false);
      }
    },
    async loadDsldByUpcCode({ commit }, upcCode) {
      commit('setDsldFoodByUpc', null);
      commit('setDsldFoodByBrandName', null);
      commit('setIsLoadingDsldFoodByUpc', false);
      commit('setErrorLoadDsldFoodByBrandName', '');
      commit('setErrorLoadDsldFoodByUpc', '');
      commit('setIsLoadingDsldFoodByBrandName', false);

      try {
        commit('setIsLoadingDsldFoodByUpc', true);
        const data = await Dataman.DB.ingredientsAndNutrients.getDsldByUPC(upcCode);

        if (data) {
          commit('setDsldFoodByUpc', data.data);
        }
      } catch (error) {
        commit('setErrorLoadDsldFoodByUpc', 'No data found');
        return;
      } finally {
        commit('setIsLoadingDsldFoodByUpc', false);
      }
    },
    async loadDsldByBrandName({ commit }, brandName) {
      commit('setDsldFoodByUpc', null);
      commit('setDsldFoodByBrandName', null);
      commit('setIsLoadingDsldFoodByUpc', false);
      commit('setErrorLoadDsldFoodByBrandName', '');
      commit('setErrorLoadDsldFoodByUpc', '');
      commit('setIsLoadingDsldFoodByBrandName', false);

      try {
        commit('setIsLoadingDsldFoodByBrandName', true);
        const data = await Dataman.DB.ingredientsAndNutrients.getDsldByBrandName(brandName);

        if (data && data.data?.hits.length) {
          commit('setDsldFoodByBrandName', data.data);
        } else {
          commit('setDsldFoodByBrandName', null);
        }
      } catch (error) {
        commit('setErrorLoadDsldFoodByBrandName', 'No data found');
      } finally {
        commit('setIsLoadingDsldFoodByBrandName', false);
      }
    },
    setActiveUserFilter({ commit }, payload) {
      commit('setActiveUserFilter', payload);
    },
    setTheme({ commit }, theme) {
      commit('setTheme', theme);
    },
  },
  getters: {
    currentPlan(state) {
      return state.currentRequest;
    },
    isLoggedIn() {
      return User.logged;
    },
    token() {
      return User.token;
    },
    fullyLoaded(state) {
      return state.flags.loaded;
    },
    counters(state) {
      return state.countersCache.counters || {};
    },
    builtAt(state) {
      return state.vars.buildTimestamp;
    },
    filtersEditorState(state) {
      return state.filterEditor.id !== null;
    },
    activeUserSession(state) {
      return state.userSessions.activeId || null;
    },
    readableCats(state) {
      return state.cachedData[CACHED_DATA_CATEGORIES]?.data?.readable || [];
    },

    // ingredientsAndNutrients state

    // food by GTIN code
    foodByGtinCode(state) {
      return state.ingredientsAndNutrients.foodByGtinCode;
    },
    isLoadingFoodByGtinCode(state) {
      return state.ingredientsAndNutrients.isLoadingFoodByGtinCode;
    },
    foodNutrients(state) {
      return state.ingredientsAndNutrients.foodByGtinCode?.foodNutrients || [];
    },
    errorLoadFoodByGtinCode(state) {
      return state.ingredientsAndNutrients.errorLoadFoodByGtinCode;
    },

    // dsld food by UPC code
    dsldFoodByUpc(state) {
      return state.ingredientsAndNutrients.dsldFoodByUpc;
    },
    isLoadingDsldFoodByUpc(state) {
      return state.ingredientsAndNutrients.isLoadingDsldFoodByUpc;
    },
    errorLoadDsldFoodByUpc(state) {
      return state.ingredientsAndNutrients.errorLoadDsldFoodByUpc;
    },

    // dsld food by brand name
    dsldFoodByBrandName(state) {
      return state.ingredientsAndNutrients.dsldFoodByBrandName?.hits.map((item) => ({ ...item._source }));
    },
    isLoadingDsldFoodByBrandName(state) {
      return state.ingredientsAndNutrients.isLoadingDsldFoodByBrandName;
    },
    errorLoadDsldFoodByBrandName(state) {
      return state.ingredientsAndNutrients.errorLoadDsldFoodByBrandName;
    },
    activeUserFilter(state) {
      return state.activeUserFilter;
    },
  },
});

export default store;

export const useStore = () => store;
